/**
 * Empresas que a sessão alcança, e qual delas está em uso.
 *
 * A leitura e a gravação estavam espalhadas: a tela de seleção montava a lista de
 * um jeito, o cabeçalho da PJ lia com JSON.parse cru e o provider de autenticação
 * gravava a empresa corrente em outro formato. Quem não passava pela tela de
 * seleção ficava sem lista nenhuma — era o caso do usuário PF com mais de uma
 * empresa, que entrava na primeira e não tinha como sair dela.
 */

const COMPANIES_KEY = 'empresas';
const SELECTED_KEY = 'selectedCompany';

/** Empresa no formato que o Select do antd consome: os campos dela mais value/label. */
export interface CompanyOption {
  [key: string]: any;
  value: string;
  label: string;
}

export const toCompanyOption = (business: any): CompanyOption => ({
  ...business,
  value: business?.id,
  label: business?.name,
});

const read = <T>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key);

    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    // Sessão aberta antes desta virada podia gravar a string "undefined" aqui, e o
    // JSON.parse dela derrubava a montagem das rotas com tela branca.
    return fallback;
  }
};

export const readCompanies = (): CompanyOption[] => {
  const stored = read<CompanyOption[] | null>(COMPANIES_KEY, null);

  return Array.isArray(stored) ? stored : [];
};

export const readSelectedCompany = (): CompanyOption | null =>
  read<CompanyOption | null>(SELECTED_KEY, null);

export const persistCompanies = (businesses: any): CompanyOption[] => {
  const companies = Array.isArray(businesses) ? businesses.map(toCompanyOption) : [];

  localStorage.setItem(COMPANIES_KEY, JSON.stringify(companies));

  return companies;
};

export const selectCompany = (company: CompanyOption): void => {
  localStorage.setItem(SELECTED_KEY, JSON.stringify(company));
};

/**
 * Áreas do portal que operam dentro de uma empresa: pessoa física, pessoa
 * jurídica e conta split.
 *
 * Nas outras — administrador, comercial, parceiro, antecipadora — é justamente a
 * ausência de empresa selecionada que faz as consultas verem tudo, porque os
 * serviços caem em `selectedCompany?.merchantId ?? null`. Gravar uma empresa
 * nessas contas estreitaria silenciosamente o que elas enxergam.
 */
const COMPANY_SCOPED_USER_TYPES = [1, 2, 8];

export const isCompanyScoped = (userType?: number): boolean =>
  userType !== undefined && COMPANY_SCOPED_USER_TYPES.includes(userType);

/**
 * Verdadeiro quando ainda há empresa a escolher — e, portanto, quando a sessão
 * vai passar pela tela de seleção.
 *
 * Com uma empresa só a sessão já entra nela, e a tela seria um clique numa lista
 * de um item.
 */
export const requiresCompanyChoice = (userType: number | undefined, businesses: any): boolean =>
  isCompanyScoped(userType) && Array.isArray(businesses) && businesses.length > 1;
